import React from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import { Formik } from 'formik';
import { DatePickerField } from 'Shared/form/DatePickerField';
import dayjs from 'dayjs';
import CustomMenuField from 'Shared/form/CustomMenu';
import SelectField from 'Shared/form/SelectField';
import SubmitButton from 'Shared/form/SubmitButton';
import { PATIENT_STATUS } from './utils/patientStatus.constants';
import CommonPatientFieldWrapper from 'App/components/PatientForm/components/CommonPatientFieldWrapper';

const propTypes = {
    onHide: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

const defaultProps = {};

const initialValues = {
    therapeuticCategoryOptOutDate: null,
    therapeuticCategoryOptOutReason: null,
    medicationEndDate: null,
    medicationEndReason: null,
    patientStatus: null,
    deceasedDate: null,
};

const patientStatusOptions = [{ ValueName: PATIENT_STATUS.INACTIVE }, { ValueName: PATIENT_STATUS.DECEASED }];

function DischargePatientModal({ onHide, onSubmit }) {
    return (
        <Modal backdrop="static" size="lg" show onHide={onHide} data-testid="discharge-patient-modal">
            <Formik
                initialValues={initialValues}
                onSubmit={(values) => {
                    // Keep dates as raw Date objects here (not pre-formatted strings), matching
                    // MedicationEndModal/TherapeuticCategoryOptOutModal and deceasedDate below,
                    // otherwise the local-formatted string round-trips through serialization
                    // differently and can shift the stored date by a day.
                    const { therapeuticCategoryOptOutDate } = values;
                    const therapeuticCategoryOptOutReason = values.therapeuticCategoryOptOutReason.valueName;
                    const { medicationEndDate } = values;
                    const medicationEndReason = values.medicationEndReason.valueName;

                    return onSubmit({
                        therapeuticCategoryOptOutDate,
                        therapeuticCategoryOptOutReason,
                        medicationEndDate,
                        medicationEndReason,
                        patientStatus: values.patientStatus,
                        deceasedDate:
                            values.patientStatus?.ValueName === PATIENT_STATUS.DECEASED ? values.deceasedDate : null,
                    });
                }}
            >
                {({ handleSubmit, values }) => {
                    return (
                        <form data-testid="discharge-form" onSubmit={handleSubmit}>
                            <Modal.Header>
                                <Modal.Title role="heading">Discharge Patient</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Row>
                                    <Col md={12}>
                                        <p>
                                            Use this modal to select the date and reason for discharging this patient
                                            from all services. These options will be applied to all active Medications
                                            and Therapeutic Categories. Upon saving, Discharge Patient will: end all
                                            active medications; opt out of all therapeutic categories; delete all
                                            pending activities (including referral activities).
                                        </p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <DatePickerField
                                            fieldName="therapeuticCategoryOptOutDate"
                                            label="Therapeutic Category Opt-Out Date"
                                            required
                                            maxDate={dayjs().toDate()}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CustomMenuField
                                            fieldName="therapeuticCategoryOptOutReason"
                                            label="Therapeutic Category Opt-Out Reason"
                                            menuName="therapy_opt_out_reasons"
                                            required
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <DatePickerField
                                            fieldName="medicationEndDate"
                                            label="Medication End Date"
                                            required
                                            maxDate={dayjs().toDate()}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <CustomMenuField
                                            fieldName="medicationEndReason"
                                            label="Medication End Reason"
                                            menuName="medication_end_reason"
                                            required
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <CommonPatientFieldWrapper fieldName="patientStatusDischarge">
                                        {(commonProps) => (
                                            <Col md={6}>
                                                <SelectField
                                                    {...commonProps}
                                                    fieldName="patientStatus"
                                                    label="Patient Status"
                                                    options={patientStatusOptions}
                                                    getOptionValue={(option) => option.ValueName}
                                                    getOptionLabel={(option) => option.ValueName}
                                                    filterOptionType="startsWith"
                                                />
                                            </Col>
                                        )}
                                    </CommonPatientFieldWrapper>
                                    {values.patientStatus?.ValueName === PATIENT_STATUS.DECEASED ? (
                                        <Col md={6}>
                                            <DatePickerField
                                                fieldName="deceasedDate"
                                                label="Deceased Date"
                                                maxDate={dayjs().toDate()}
                                            />
                                        </Col>
                                    ) : null}
                                </Row>
                            </Modal.Body>
                            <Modal.Footer>
                                <FormButtonGroup>
                                    <Button variant="outline-secondary" onClick={onHide} type="button">
                                        Cancel
                                    </Button>
                                    <SubmitButton variant="primary" type="button">
                                        Discharge
                                    </SubmitButton>
                                </FormButtonGroup>
                            </Modal.Footer>
                        </form>
                    );
                }}
            </Formik>
        </Modal>
    );
}

DischargePatientModal.propTypes = propTypes;
DischargePatientModal.defaultProps = defaultProps;

export default DischargePatientModal;
